import { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import {
  sendEmail,
  draftEmail,
  fetchSendGuard,
  searchDeals,
  ApiError,
  type SendResult,
  type SendGuard,
  type DealSearchHit,
} from "../../api";
import { ContactPicker, type Recipient } from "./ContactPicker";
import { buildOutgoingEmailHtml } from "@edge/shared";

/** WD-2.1 — a WorkDrive file pre-attached when composing from the WorkDrive
 *  tab's multi-select. Bytes are pulled server-side at send by file id. */
export interface ComposeAttachment {
  fileId: string;
  name: string;
  sizeBytes: number;
}

interface Props {
  onClose: () => void;
  onSent: (result: SendResult) => void;
  /** Pre-fill the To field — e.g. a deal's borrower when composing from the
   *  focus Email tab (QW-8). */
  initialTo?: Recipient[];
  /** WD-2.1 — WorkDrive files to attach (from the WorkDrive tab). */
  initialAttachments?: ComposeAttachment[];
  /** Deal context for the attach-from-WorkDrive path. */
  dealId?: string;
}

// Over this, the server switches from the fast inline path to Graph's draft +
// upload-session path (slower but supports large files). Not a block — just a
// heads-up that the send may take a bit.
const INLINE_HINT_BYTES = 3 * 1024 * 1024;
// Hard ceiling — mirrors the server's LARGE_ATTACH_CAP_BYTES so the user gets
// immediate feedback instead of a post-send 413.
const ATTACH_CAP_BYTES = 60 * 1024 * 1024;

function fmtBytes(n: number): string {
  if (!n) return "—";
  const u = ["B", "KB", "MB", "GB"];
  let v = n;
  let i = 0;
  while (v >= 1024 && i < u.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
}

type AiMode = "generate" | "polish" | "edit";

function fmtR(r: Recipient): string {
  return r.name ? `${r.name} <${r.email}>` : r.email;
}

/**
 * New-message composer (Piece 2, Compose) + the same approve-before-send gate
 * as replies. To/Cc use the unified contact picker (CRM borrowers + lenders +
 * free-type). The AI bar reuses the 3 draft modes in "new" kind (generate works
 * from a brief). Output is just text — it flows the gated POST /api/email/send,
 * which holds the server-side send guard. The AI never sends.
 */
export function EmailComposeModal({
  onClose,
  onSent,
  initialTo,
  initialAttachments,
  dealId,
}: Props) {
  const [to, setTo] = useState<Recipient[]>(initialTo ?? []);
  const [attachments, setAttachments] = useState<ComposeAttachment[]>(
    initialAttachments ?? [],
  );
  const [cc, setCc] = useState<Recipient[]>([]);
  const [showCc, setShowCc] = useState(false);
  const [subject, setSubject] = useState("");
  const [text, setText] = useState("");

  const [aiBusy, setAiBusy] = useState<AiMode | "docs" | null>(null);
  const [aiError, setAiError] = useState<string | null>(null);
  const [instruction, setInstruction] = useState("");
  // EM-6 #1: link a deal while composing (cockpit compose has no deal context)
  // so From-docs + deal-scoped features work on a brand-new email.
  const [chosenDeal, setChosenDeal] = useState<{ id: string; name: string } | null>(null);
  const [dealPicking, setDealPicking] = useState(false);
  const [dealQuery, setDealQuery] = useState("");
  const [dealHits, setDealHits] = useState<DealSearchHit[]>([]);
  const [dealSearching, setDealSearching] = useState(false);
  const dealReqRef = useRef(0);
  // Effective deal: the host-provided one (focus/WorkDrive) or the one picked here.
  const effectiveDealId = dealId ?? chosenDeal?.id;
  // EM-1: which docs the last "From docs" draft used / looked at.
  const [docInfo, setDocInfo] = useState<{
    used: string[];
    looked_at: string[];
    no_match: boolean;
  } | null>(null);

  const [confirming, setConfirming] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [guard, setGuard] = useState<SendGuard | null>(null);
  const idemRef = useRef<string>(
    typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : String(Date.now()),
  );

  useEffect(() => {
    fetchSendGuard().then(setGuard).catch(() => {});
  }, []);

  // EM-6 #1: debounced deal search while picking (stale-guarded).
  useEffect(() => {
    if (!dealPicking) return;
    const q = dealQuery.trim();
    if (q.length < 2) {
      setDealHits([]);
      setDealSearching(false);
      return;
    }
    setDealSearching(true);
    const myReq = ++dealReqRef.current;
    const t = setTimeout(async () => {
      try {
        const { deals } = await searchDeals(q, true);
        if (myReq === dealReqRef.current) setDealHits(deals);
      } catch {
        if (myReq === dealReqRef.current) setDealHits([]);
      } finally {
        if (myReq === dealReqRef.current) setDealSearching(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [dealQuery, dealPicking]);

  const composeContext = {
    to: [...to, ...cc].map(fmtR).join(", "),
    subject,
  };

  const runAi = async (mode: AiMode, useDocs = false) => {
    setAiBusy(useDocs ? "docs" : mode);
    setAiError(null);
    if (useDocs) setDocInfo(null);
    try {
      const { draft, docs } = await draftEmail({
        mode,
        kind: "new",
        compose_context: composeContext,
        current_draft: text.trim() || undefined,
        instruction: instruction.trim() || undefined,
        use_docs: useDocs || undefined,
        deal_id: useDocs ? effectiveDealId : undefined,
      });
      setText(draft);
      if (mode === "edit") setInstruction("");
      if (useDocs && docs) setDocInfo(docs);
    } catch (err) {
      setAiError(
        err instanceof ApiError
          ? `${err.code} (HTTP ${err.status})`
          : err instanceof Error
            ? err.message
            : "draft_failed",
      );
    } finally {
      setAiBusy(null);
    }
  };

  const attachTotal = attachments.reduce((s, a) => s + a.sizeBytes, 0);
  const attachTooLarge = attachTotal > ATTACH_CAP_BYTES;
  const attachLarge = attachTotal > INLINE_HINT_BYTES && !attachTooLarge;
  const canSend =
    to.length > 0 && subject.trim() !== "" && text.trim() !== "" && !attachTooLarge;

  const send = async () => {
    setSending(true);
    setError(null);
    try {
      const result = await sendEmail({
        to,
        cc,
        subject: subject.trim(),
        body: text.trim(),
        idempotency_key: idemRef.current,
        workdrive_files: attachments.length
          ? attachments.map((a) => ({
              fileId: a.fileId,
              name: a.name,
              sizeBytes: a.sizeBytes,
            }))
          : undefined,
        deal_id: effectiveDealId,
        // EM-6 #2 — only an EXPLICIT pick tags the thread to a deal (writes the
        // durable association); the focus tab's implicit deal isn't auto-tagged.
        tag_deal_id: chosenDeal?.id,
        tag_deal_name: chosenDeal?.name,
      });
      onSent(result);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? `${err.code} (HTTP ${err.status})`
          : err instanceof Error
            ? err.message
            : "send_failed",
      );
      setSending(false);
    }
  };

  return (
    <div className="write-confirm-backdrop nested-modal" role="dialog" aria-modal="true">
      <div className="write-confirm-card email-reply-card email-compose-card">
        <div className="email-detail-header">
          <div className="write-confirm-title">{confirming ? "CONFIRM SEND" : "NEW EMAIL"}</div>
          <button className="email-detail-close" onClick={onClose} title="Close (Esc)">
            ✕
          </button>
        </div>

        {guard?.active && (
          <div className="email-guard-banner">
            ⚠ SEND GUARD ACTIVE — every send is redirected to <b>{guard.redirect_to}</b>. No
            email will reach the real recipient.
          </div>
        )}

        {!confirming ? (
          // ── Compose ──
          <div className="email-reply-body">
            <ContactPicker label="To" recipients={to} onChange={setTo} />
            {showCc ? (
              <ContactPicker label="Cc" recipients={cc} onChange={setCc} />
            ) : (
              <button className="email-compose-addcc" onClick={() => setShowCc(true)}>
                + Add Cc
              </button>
            )}

            <div className="email-compose-subject-row">
              <span className="contact-picker-label">Subj</span>
              <input
                className="email-compose-subject"
                placeholder="Subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
            </div>

            {/* EM-6 #2 — tag this new thread to a deal: writes a durable
                association so the email + its replies flow into that deal's
                Email tab (and enables From-docs). Cockpit compose only. */}
            {!dealId && (
              <div className="email-compose-deal">
                {chosenDeal ? (
                  <span className="email-compose-deal-set">
                    🏷 Tagged to: <b>{chosenDeal.name}</b>
                    <span className="email-compose-deal-hint">
                      {" "}
                      — this thread + replies will show in its Email tab
                    </span>
                    <button
                      className="email-att-wd-change"
                      onClick={() => setChosenDeal(null)}
                    >
                      change
                    </button>
                  </span>
                ) : (
                  <div className="email-att-wd">
                    <button
                      className="email-compose-deal-link"
                      onClick={() => setDealPicking((v) => !v)}
                    >
                      🏷 Tag to a deal (flows this thread into its Email tab)
                    </button>
                    {dealPicking && (
                      <div className="email-att-wd-picker">
                        <input
                          className="email-att-wd-search"
                          autoFocus
                          placeholder="Search a deal…"
                          value={dealQuery}
                          onChange={(e) => setDealQuery(e.target.value)}
                        />
                        {dealSearching ? (
                          <div className="email-att-wd-empty">Searching…</div>
                        ) : dealQuery.trim().length < 2 ? (
                          <div className="email-att-wd-empty">Type 2+ characters</div>
                        ) : dealHits.length === 0 ? (
                          <div className="email-att-wd-empty">No deals</div>
                        ) : (
                          dealHits.map((h) => (
                            <button
                              key={h.id}
                              className="email-att-wd-folder"
                              title={h.name}
                              onClick={() => {
                                setChosenDeal({ id: h.id, name: h.name });
                                setDealPicking(false);
                                setDealQuery("");
                                setDealHits([]);
                              }}
                            >
                              {h.name}
                              {h.closed ? " (closed)" : ""}
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="email-reply-ai">
              <div className="email-reply-ai-row">
                <input
                  className="email-reply-ai-input"
                  placeholder="Brief for “Draft”, or a change for “Revise”…"
                  value={instruction}
                  onChange={(e) => setInstruction(e.target.value)}
                  disabled={!!aiBusy}
                />
              </div>
              <div className="email-reply-ai-row">
                <span className="email-reply-ai-label">✨ AI</span>
                <button
                  className="email-reply-ai-btn"
                  onClick={() => void runAi("generate")}
                  disabled={!!aiBusy || !instruction.trim()}
                  title={
                    instruction.trim()
                      ? "AI writes a full email from your brief above"
                      : "Type a brief above first (what the email should say)"
                  }
                >
                  {aiBusy === "generate" ? "Drafting…" : "Draft from brief"}
                </button>
                <button
                  className="email-reply-ai-btn"
                  onClick={() => void runAi("polish")}
                  disabled={!!aiBusy || !text.trim()}
                  title={text.trim() ? "Professionalize your draft" : "Write a rough draft first"}
                >
                  {aiBusy === "polish" ? "Polishing…" : "Polish"}
                </button>
                <button
                  className="email-reply-ai-btn"
                  onClick={() => void runAi("edit")}
                  disabled={!!aiBusy || !text.trim() || !instruction.trim()}
                  title={
                    !text.trim()
                      ? "Draft something first"
                      : !instruction.trim()
                        ? "Type a change above"
                        : "AI revises the draft and returns the full text"
                  }
                >
                  {aiBusy === "edit" ? "Revising…" : "Revise"}
                </button>
                {effectiveDealId && (
                  <button
                    className="email-reply-ai-btn email-reply-ai-docs"
                    onClick={() => void runAi("generate", true)}
                    disabled={!!aiBusy || !instruction.trim()}
                    title={
                      instruction.trim()
                        ? "AI reads the deal's documents and drafts using them"
                        : "Type what to write/find above (e.g. “confirm the appraised value”)"
                    }
                  >
                    {aiBusy === "docs" ? "📄 Reading documents…" : "📄 From docs"}
                  </button>
                )}
              </div>
              {aiBusy === "docs" && (
                <div className="email-reply-ai-docnote">
                  Reading the deal’s documents… scanned files can take ~20s.
                </div>
              )}
              {docInfo && aiBusy !== "docs" && (
                <div className="email-reply-ai-docsrc">
                  {docInfo.used.length > 0
                    ? `📄 Drafted using: ${docInfo.used.join(", ")}`
                    : docInfo.no_match
                      ? "📄 No matching document found — drafted from your brief. Try naming the doc or being more specific."
                      : `📄 Looked at ${docInfo.looked_at.join(", ")} but couldn’t read them — drafted from your brief.`}
                </div>
              )}
              {aiError && <div className="email-reply-ai-error">⚠ AI: {aiError}</div>}
            </div>

            <textarea
              className="email-reply-textarea"
              placeholder="Write your email, or use AI above…"
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={9}
            />
            <div className="email-reply-sig-note">
              Your WestEdge signature is appended automatically on send.
            </div>

            {attachments.length > 0 && (
              <div className="compose-attach">
                <div className="compose-attach-head">
                  📎 {attachments.length} attachment{attachments.length === 1 ? "" : "s"}
                  <span
                    className={clsx("compose-attach-total", attachTooLarge && "over")}
                  >
                    {fmtBytes(attachTotal)}
                  </span>
                </div>
                <div className="compose-attach-chips">
                  {attachments.map((a) => (
                    <span key={a.fileId} className="compose-attach-chip" title={a.name}>
                      <span className="compose-attach-name">{a.name}</span>
                      <span className="compose-attach-size">{fmtBytes(a.sizeBytes)}</span>
                      <button
                        className="compose-attach-x"
                        onClick={() =>
                          setAttachments((prev) =>
                            prev.filter((x) => x.fileId !== a.fileId),
                          )
                        }
                        title="Remove attachment"
                      >
                        ✕
                      </button>
                    </span>
                  ))}
                </div>
                {attachTooLarge ? (
                  <div className="compose-attach-warn">
                    ⚠ Over the 60 MB limit — remove some files.
                  </div>
                ) : attachLarge ? (
                  <div className="compose-attach-note">
                    Large attachment — sending may take up to a minute.
                  </div>
                ) : null}
              </div>
            )}

            {error && <div className="write-confirm-error">⚠ {error}</div>}

            <div className="email-detail-actions">
              <button className="email-detail-close-btn" onClick={onClose}>
                CANCEL
              </button>
              <button
                className="write-confirm-confirm"
                disabled={!canSend}
                onClick={() => setConfirming(true)}
                title={canSend ? "Review before sending" : "Add a recipient, subject, and body"}
              >
                REVIEW & SEND →
              </button>
            </div>
          </div>
        ) : (
          // ── Approve-before-send gate ──
          <div className="email-reply-body">
            <div className="email-reply-gate-note">
              This is exactly what will be sent. Nothing has been sent yet.
            </div>
            <div className="email-reply-meta">
              <div className="email-detail-row">
                <span className="email-detail-label">To</span>
                <span className="email-detail-value">{to.map(fmtR).join(", ")}</span>
              </div>
              {cc.length > 0 && (
                <div className="email-detail-row">
                  <span className="email-detail-label">Cc</span>
                  <span className="email-detail-value">{cc.map(fmtR).join(", ")}</span>
                </div>
              )}
              <div className="email-detail-row">
                <span className="email-detail-label">Subject</span>
                <span className="email-detail-value email-detail-subject">{subject}</span>
              </div>
              {attachments.length > 0 && (
                <div className="email-detail-row">
                  <span className="email-detail-label">Files</span>
                  <span className="email-detail-value">
                    📎 {attachments.map((a) => a.name).join(", ")} ({fmtBytes(attachTotal)})
                  </span>
                </div>
              )}
            </div>

            {/* WYSIWYG: render the COMPLETE outgoing body — escaped text +
                thank-you closing + full signature — from the SAME @edge/shared
                builder the worker send path uses, on a white "paper" surface so
                it's byte-identical to what actually lands in the recipient's
                inbox. text.trim() matches what send() submits. */}
            <div
              className="email-html-preview"
              dangerouslySetInnerHTML={{
                __html: buildOutgoingEmailHtml(text.trim()),
              }}
            />

            {error && <div className="write-confirm-error">⚠ {error}</div>}

            <div className="email-detail-actions">
              <button
                className="email-detail-close-btn"
                onClick={() => setConfirming(false)}
                disabled={sending}
              >
                ← BACK
              </button>
              <button
                className={clsx("write-confirm-confirm", guard?.active && "is-guarded")}
                onClick={() => void send()}
                disabled={sending}
              >
                {sending
                  ? "SENDING…"
                  : guard?.active
                    ? `SEND (→ ${guard.redirect_to})`
                    : "SEND"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
